import React, { useEffect, useMemo, useState } from 'react';
import { getBucketRules, BucketRulesResponse, BucketRule } from '../api/strategy';
import './StrategyReports.css';

// Owner 2026-10-08: "Rules" tab on the Bet buckets page. Read-only explanation of every bucket: how it picks,
// when it is decided and sent, the gates/culls that apply, staking and how results are measured.
// Gates, Live state and stakes come from the same settings Auto uses (/api/strategy/bucket-rules).
const pill = (bg: string, fg: string, txt: string) => <span className="sr-pill" style={{ background: bg, color: fg }}>{txt}</span>;
const row: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(90px,140px) 1fr', gap: '4px 12px', margin: '6px 0' };
const label: React.CSSProperties = { fontWeight: 600, color: '#374151' };

const RuleCard: React.FC<{ b: BucketRule; open: boolean }> = ({ b, open }) => (
  <details open={open} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', margin: '8px 0', background: '#fff' }}>
    <summary style={{ cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <strong>{b.label}</strong>
      {b.live ? pill('#dcfce7', '#166534', 'LIVE') : pill('#f3f4f6', '#6b7280', 'paper')}
      {pill('#eef2ff', '#3730a3', b.market)}
      {b.gates.length > 0 && pill('#fef3c7', '#92400e', `${b.gates.length} gate${b.gates.length > 1 ? 's' : ''}`)}
      <span style={{ color: '#9ca3af', fontSize: 12 }}>{b.policy_id}</span>
    </summary>
    <div style={row}><span style={label}>How it picks</span><span>{b.picks}</span></div>
    {b.recorded_rule && b.recorded_rule !== b.picks && <div style={row}><span style={label}>Recorded rule</span><span style={{ color: '#4b5563' }}>{b.recorded_rule}</span></div>}
    <div style={row}><span style={label}>Timing</span><span>{b.timing}</span></div>
    <div style={row}><span style={label}>Gates &amp; culls</span>
      <span>{b.gates.length ? <ul style={{ margin: 0, paddingLeft: 18 }}>{b.gates.map((g, i) => <li key={i}><strong>{g.name}:</strong> {g.rule}</li>)}</ul> : 'None specific to this bucket (the Auto-wide rules above still apply).'}</span></div>
    <div style={row}><span style={label}>Staking</span><span>{b.staking}</span></div>
    <div style={row}><span style={label}>Measured as</span><span><strong>{b.measured.roi_label}</strong> · {b.measured.status.replace(/_/g, ' ').toLowerCase()}<br /><span style={{ color: '#4b5563' }}>{b.measured.note}</span></span></div>
    <div style={row}><span style={label}>Recent picks</span><span>{b.bets_21d} in the daily builds over the last 21 days{b.bets_21d === 0 ? ' (observer buckets record their picks separately)' : ''}</span></div>
  </details>
);

const BucketRules: React.FC = () => {
  const [data, setData] = useState<BucketRulesResponse | null>(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [liveOnly, setLiveOnly] = useState(false);
  useEffect(() => { getBucketRules().then(setData).catch((e) => setError(e.message || 'Could not load bucket rules')); }, []);
  const shown = useMemo(() => (data?.buckets || []).filter((b) => (!liveOnly || b.live) &&
    (!q || `${b.label} ${b.policy_id} ${b.picks}`.toLowerCase().includes(q.toLowerCase()))), [data, q, liveOnly]);
  if (error) return <div className="sr-error">{error}</div>;
  if (!data) return <div className="sr-muted">Loading bucket rules…</div>;
  const a = data.auto;
  return (
    <section className="sr-section">
      <p className="sr-muted" style={{ marginTop: 0 }}>
        How every bucket picks, when it is decided and sent, the gates and culls it runs through, how it is staked and how its results are measured.
        Live state, gates and stakes are read from the current settings. Auto now: {a.mode || '—'}{a.armed ? ', armed' : ', not armed'} · WIN ${a.win_stake ?? '—'} · PLACE ${a.place_stake ?? '—'} · Brain ${a.brain_stake ?? '—'} · Longshot ${a.longshot_stake ?? 'WIN stake'}.
      </p>
      <details open style={{ border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: 8, padding: '8px 12px', margin: '8px 0' }}>
        <summary style={{ cursor: 'pointer' }}><strong>Rules that apply to every Live bucket (Auto)</strong></summary>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{data.universal.map((g, i) => <li key={i}><strong>{g.name}:</strong> {g.rule}</li>)}</ul>
      </details>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0 4px' }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buckets" style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, minWidth: 200, flex: '1 1 200px', maxWidth: 360 }} />
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={liveOnly} onChange={(e) => setLiveOnly(e.target.checked)} /> Live only</label>
        <span className="sr-muted">{shown.length} of {data.buckets.length} buckets</span>
      </div>
      {shown.map((b) => <RuleCard key={b.policy_id} b={b} open={b.live || !!q} />)}
    </section>
  );
};

export default BucketRules;
